import type { UploadedImage } from '@prezly/uploadcare';
import classNames from 'classnames';
import { MoveRightIcon } from 'lucide-react';
import type { MouseEventHandler, ReactNode } from 'react';

import { Link } from '@/src/components/Link/Link';
import { VoidLink } from '@/src/components/Link/VoidLink';
import { Image } from '@/src/ui/Image/Image';
import type { ImageEffects } from '@/src/ui/Image/types';
import type { DataAttributes, Media } from '@/types';
import { slugify } from '@/util/strings/slugify';

import styles from './Card.module.scss';

interface Props extends DataAttributes {
    className?: string;
    chipText?: string;
    description?: string;
    doNotWrapWithLink?: boolean;
    readMoreLabel?: string;
    image?: Media;
    imageEffects?: ImageEffects;
    imageAddon?: ReactNode;
    path: string;
    readMoreUrl?: string;
    title: string;
    onClick?: MouseEventHandler;
}

const DEFAULT_SIZES = '450px, (min-width: 768px) 350px, (min-width: 992px) 380px';

export function Card({
    className,
    chipText,
    description,
    doNotWrapWithLink,
    image,
    imageEffects,
    imageAddon,
    readMoreLabel,
    path,
    readMoreUrl,
    title,
    onClick,
    ...attributes
}: Props) {
    const content = (
        <>
            {image && image.uploadcare && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                    data-reference-card-image
                    className={styles.imageWrapper}
                    onClick={typeof readMoreLabel === 'string' ? onClick : undefined}
                >
                    <Image
                        loading="lazy"
                        className={styles.image}
                        alt={title ?? ''}
                        src={image.uploadcare as unknown as UploadedImage}
                        sizes={DEFAULT_SIZES}
                        effects={imageEffects}
                    />
                    {imageAddon}
                </div>
            )}

            <div className={styles.content}>
                {chipText && (
                    <div className={styles.chip} data-reference-card-chip={slugify(chipText)}>
                        {chipText}
                    </div>
                )}

                {readMoreUrl ? (
                    <Link className={styles.titleWrapper} href={readMoreUrl ?? path}>
                        <h5 data-reference-card-title className={styles.title}>
                            {title}
                        </h5>
                    </Link>
                ) : (
                    <VoidLink labelClassName={styles.titleWrapper}>
                        <h5 data-reference-card-title className={styles.title}>
                            {title}
                        </h5>
                    </VoidLink>
                )}

                {description && (
                    <p className={styles.description} data-reference-card-description>
                        {description}
                    </p>
                )}

                {readMoreLabel && (
                    <div className={styles.readMoreWrapper}>
                        {readMoreUrl && (
                            <Link
                                data-reference-card-read-more
                                href={readMoreUrl}
                                className={classNames(styles.readMore, styles.link)}
                            >
                                {readMoreLabel}
                                <MoveRightIcon className={styles.readMoreIcon} />
                            </Link>
                        )}
                        {!readMoreUrl && (
                            <VoidLink
                                data-reference-card-read-more
                                iconRight={MoveRightIcon}
                                className={classNames(styles.readMore, styles.link)}
                            >
                                {readMoreLabel}
                            </VoidLink>
                        )}
                    </div>
                )}
            </div>
        </>
    );

    if (typeof readMoreUrl === 'string' || doNotWrapWithLink) {
        return (
            <div
                data-reference-card={slugify(title)}
                {...attributes}
                className={classNames(styles.card, className)}
            >
                {content}
            </div>
        );
    }

    return (
        <Link
            data-reference-card={slugify(title)}
            {...attributes}
            href={path}
            className={classNames(styles.card, className)}
            onClick={onClick}
        >
            {content}
        </Link>
    );
}
